import z from 'zod'

/**
 * Creates a Zod schema for parsing boolean-like string values.
 *
 * This function returns a Zod schema that can parse string representations
 * of boolean values and transform them into actual boolean values.
 *
 * @returns A Zod schema for boolean-like string validation and transformation
 *
 * @example
 * ```ts
 * const boolSchema = Bool();
 * boolSchema.parse('true');  // returns true
 * boolSchema.parse('1');     // returns true
 * boolSchema.parse('false'); // returns false
 * boolSchema.parse('0');     // returns false
 * ```
 */
export const Bool = (): z.ZodType<boolean, string> => BaseBool.clone()

/**
 * Base Zod schema for boolean-like string validation and transformation.
 *
 * Validates that a string value is a valid boolean representation
 * and transforms it to the corresponding boolean value.
 *
 * @private
 */
const BaseBool: z.ZodType<boolean, string> = z
  .stringFormat('boolean-like', value =>
    Object.hasOwn(MAP, value.toLowerCase()),
  )
  .transform(value => MAP[value.toLowerCase() as keyof typeof MAP])

/**
 * Mapping of string representations to boolean values.
 *
 * This map defines which string values are considered valid
 * boolean representations and their corresponding boolean values.
 *
 * @private
 * @constant
 */
const MAP = {
  true: true,
  false: false,
  '1': true,
  '0': false,
} as const
